import { useState, useMemo, useEffect } from 'react';
import { Check, Search, FlaskConical } from 'lucide-react';
import cx from 'classnames';
import { useTestingApi } from '../hooks/useTestingApi';
import { ITestDefinition } from '../../models/testDefinition';
import GenericModalV2 from '../../shared/GenericModal/GenericModal';
import CustomButton from '../../shared/CustomButton/CustomButton';
import Typhography from '../../shared/Typhography/Typhography';
import styles from './TestConfigModal.module.scss';

interface TestFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  hiddenTestIds: Set<string>;
  onApply: (hiddenTestIds: Set<string>) => void;
}

const categoryLabels: Record<string, string> = {
  forza: 'Forza',
  salto: 'Salto',
  velocita: 'Velocità',
  resistenza: 'Resistenza',
  mobilita: 'Mobilità',
  composizione: 'Composizione Corporea',
  rtp_rts: 'RTP/RTS',
};

const categoryOrder = ['forza', 'salto', 'velocita', 'resistenza', 'mobilita', 'composizione', 'rtp_rts'];

export default function TestFilterModal({ isOpen, onClose, hiddenTestIds, onApply }: TestFilterModalProps) {
  const [localHidden, setLocalHidden] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const { catalog } = useTestingApi();

  useEffect(() => {
    if (isOpen) {
      setLocalHidden(new Set(hiddenTestIds));
      setSearchQuery('');
      setActiveCategory(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const isTestVisible = (test: ITestDefinition) =>
    !localHidden.has(test.id) && !localHidden.has(test._id);

  const toggleTest = (test: ITestDefinition) => {
    setLocalHidden((prev) => {
      const next = new Set(prev);
      if (next.has(test.id)) {
        next.delete(test.id);
      } else {
        next.add(test.id);
      }
      return next;
    });
  };

  const showAll = (tests: ITestDefinition[]) => {
    setLocalHidden((prev) => {
      const next = new Set(prev);
      tests.forEach((t) => {
        next.delete(t.id);
        next.delete(t._id);
      });
      return next;
    });
  };

  const hideAll = (tests: ITestDefinition[]) => {
    setLocalHidden((prev) => {
      const next = new Set(prev);
      tests.forEach((t) => next.add(t.id));
      return next;
    });
  };

  const handleApply = () => {
    onApply(localHidden);
    onClose();
  };

  const groupedCatalog = useMemo(() => {
    const filtered = catalog.filter((test: ITestDefinition) =>
      test.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    const groups: Record<string, ITestDefinition[]> = {};
    filtered.forEach((test: ITestDefinition) => {
      if (!groups[test.category]) groups[test.category] = [];
      groups[test.category].push(test);
    });
    return groups;
  }, [catalog, searchQuery]);

  const sortedCategories = useMemo(() => {
    return categoryOrder.filter((c) => groupedCatalog[c]);
  }, [groupedCatalog]);

  const visibleCount = catalog.length - localHidden.size;
  const totalCount = catalog.length;

  const visibleByCategory = useMemo(() => {
    const counts: Record<string, number> = {};
    catalog.forEach((test: ITestDefinition) => {
      if (!counts[test.category]) counts[test.category] = 0;
      if (isTestVisible(test)) counts[test.category]++;
    });
    return counts;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, localHidden]);

  const visibleCategories = activeCategory
    ? sortedCategories.filter((c) => c === activeCategory)
    : sortedCategories;

  return (
    <GenericModalV2
      isOpen={isOpen}
      onClose={onClose}
      title="Filtra test"
      size="xl"
      Footer={
        <div className={styles.footerRow}>
          <div className={styles.footerCount}>
            <FlaskConical size={14} />
            <span>{visibleCount} / {totalCount} test visibili</span>
          </div>
          <div className={styles.footerActions}>
            <CustomButton color="outline" onClick={() => setLocalHidden(new Set(catalog.map((t: ITestDefinition) => t.id)))}>
              Rimuovi tutti
            </CustomButton>
            <CustomButton color="outline" onClick={onClose}>
              Annulla
            </CustomButton>
            <CustomButton color="secondary" onClick={handleApply}>
              Applica
            </CustomButton>
          </div>
        </div>
      }
    >
      <div className={styles.root}>
        {/* Search */}
        <div className={styles.searchWrapper}>
          <Search className={styles.searchIcon} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Cerca test..."
            className={styles.searchInput}
          />
        </div>

        {/* Category pills */}
        <div className={styles.categoryPills}>
          <button
            className={cx(styles.categoryPill, !activeCategory && styles.categoryPillActive)}
            onClick={() => setActiveCategory(null)}
          >
            Tutti
            <span className={styles.pillCount}>{visibleCount}</span>
          </button>
          {sortedCategories.map((cat) => (
            <button
              key={cat}
              className={cx(styles.categoryPill, activeCategory === cat && styles.categoryPillActive)}
              onClick={() => setActiveCategory(activeCategory === cat ? null : cat)}
            >
              {categoryLabels[cat] ?? cat}
              {(visibleByCategory[cat] ?? 0) > 0 && (
                <span className={styles.pillCount}>{visibleByCategory[cat]}</span>
              )}
            </button>
          ))}
        </div>

        {/* Test list */}
        <div className={styles.testListScroll}>
          {visibleCategories.map((category) => {
            const tests = groupedCatalog[category];
            const allVisible = tests.every((t) => isTestVisible(t));
            return (
              <div key={category} className={styles.categorySection}>
                <div className={styles.categoryHeader}>
                  <Typhography size="bold-12" className="color-text-1">
                    {categoryLabels[category] ?? category}
                  </Typhography>
                  <button
                    className={styles.selectAllBtn}
                    onClick={() => allVisible ? hideAll(tests) : showAll(tests)}
                  >
                    {allVisible ? 'Nascondi tutti' : 'Mostra tutti'}
                  </button>
                </div>
                <div className={styles.testGrid}>
                  {tests.map((test: ITestDefinition) => {
                    const visible = isTestVisible(test);
                    return (
                      <label key={test._id} className={cx(styles.testItem, visible && styles.testItemEnabled)}>
                        <input
                          type="checkbox"
                          checked={visible}
                          onChange={() => toggleTest(test)}
                          className={styles.hiddenInput}
                        />
                        <div className={cx(styles.checkbox, visible && styles.checkboxChecked)}>
                          {visible && <Check size={12} strokeWidth={3} />}
                        </div>
                        <div className={styles.testInfo}>
                          <span className={styles.testName}>{test.name}</span>
                          <span className={styles.testUnit}>{test.defaultUnit}</span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {sortedCategories.length === 0 && (
            <div className={styles.emptyState}>
              <Typhography size="regular-14" className="color-text-3">
                {catalog.length === 0 ? 'Caricamento catalogo...' : 'Nessun test trovato'}
              </Typhography>
            </div>
          )}
        </div>
      </div>
    </GenericModalV2>
  );
}
